/**
 * GeneratePanel — завантаження матеріалу й генерація карток через Gemini.
 *
 * Приймає текст конспекту, PDF або скриншоти (Base64 → Gemini inline_data).
 * Предмет і тему НЕ питаємо в користувача: Gemini визначає їх за змістом
 * матеріалу й повертає разом із картками — далі їх видно й можна поправити
 * в PreviewModal перед збереженням.
 */

import { useCallback, useRef, useState } from 'react'
import {
  BrainCircuit,
  FileText,
  Image as ImageIcon,
  KeyRound,
  Loader2,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'

import { Field, Alert } from './ui.jsx'
import { api, fileToDataUrl } from '../lib/api.js'
import { LIMITS } from '../lib/constants.js'

const MAX_PREVIEW_FILES = 12

const PLACEHOLDER =
  'Вставте конспект, тему або питання — або додайте файл. Предмет і тему Gemini визначить сам.'

export default function GeneratePanel({
  settings,
  deckId = null,
  onGenerated,
  onNeedKeys,
}) {
  const [text, setText] = useState('')
  const [extra, setExtra] = useState('')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [dragging, setDragging] = useState(false)
  const abortRef = useRef(null)
  const fileInputRef = useRef(null)

  const hasKeys = (settings?.geminiKeys?.length ?? 0) > 0

  const addFiles = useCallback(async (fileList) => {
    const incoming = Array.from(fileList ?? [])
    if (!incoming.length) return

    const room = MAX_PREVIEW_FILES - files.length
    if (room <= 0) {
      setError(`Максимум ${MAX_PREVIEW_FILES} файлів за раз.`)
      return
    }

    const accepted = []
    const rejected = []

    for (const file of incoming.slice(0, room)) {
      const okType =
        file.type === 'application/pdf' || /^image\/(png|jpe?g|webp|gif|heic|heif)$/.test(file.type)
      if (!okType) {
        rejected.push(`${file.name} — не PDF чи зображення`)
        continue
      }
      if (file.size > LIMITS.maxFileBytes) {
        rejected.push(
          `${file.name} — ${(file.size / 1024 / 1024).toFixed(1)} МБ, максимум ${
            LIMITS.maxFileBytes / 1024 / 1024
          } МБ`,
        )
        continue
      }
      try {
        const dataUrl = await fileToDataUrl(file)
        accepted.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl,
          isPdf: file.type === 'application/pdf',
        })
      } catch {
        rejected.push(`${file.name} — не вдалося прочитати`)
      }
    }

    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_PREVIEW_FILES))
    setError(rejected.length ? `Деякі файли пропущено: ${rejected.join('; ')}` : null)
  }, [files.length])

  const generate = useCallback(async () => {
    if (!hasKeys) {
      onNeedKeys?.()
      return
    }
    if (!text.trim() && !files.length) {
      setError('Додайте текст конспекту або файл (PDF / скриншот).')
      return
    }

    setBusy(true)
    setError(null)
    const ctrl = new AbortController()
    abortRef.current = ctrl

    try {
      const res = await api.generate(
        {
          keys: settings.geminiKeys,
          text: text.slice(0, LIMITS.maxTextChars),
          files: files.map((f) => ({ name: f.name, dataUrl: f.dataUrl })),
          deckId,
          sourceLabel: files.length === 1 ? files[0].name : null,
          model: settings.geminiModel,
          extra,
        },
        { signal: ctrl.signal },
      )

      if (!res.cards?.length) {
        setError('Gemini не запропонував жодної картки. Спробуйте інший шматок матеріалу.')
        return
      }

      onGenerated?.({
        cards: res.cards,
        // Визначене моделлю: користувач не вводив нічого з цього
        subject: res.subject ?? 'математика',
        subjectLabel: res.subjectLabel ?? null,
        subjectSource: res.subjectSource ?? 'fallback',
        topic: res.topic ?? null,
        topicTag: res.topicTag ?? null,
        totalCardsGenerated: res.totalCardsGenerated ?? res.cards.length,
        suggestedTags: res.suggestedTags ?? [],
        warnings: res.warnings ?? [],
        sourceLabel: files.length === 1 ? files[0].name : null,
        usedKey: res.usedKey,
        attempts: res.attempts,
        model: res.model,
      })
    } catch (err) {
      if (err?.status === 400 && err?.payload?.needsKeys) {
        onNeedKeys?.()
        setError('Потрібно додати Gemini API-ключі в налаштуваннях.')
      } else {
        setError(err?.message ?? 'Не вдалося згенерувати картки.')
      }
    } finally {
      setBusy(false)
      abortRef.current = null
    }
  }, [hasKeys, text, files, extra, deckId, settings, onGenerated, onNeedKeys])

  const totalSize = files.reduce((sum, f) => sum + f.size, 0)

  return (
    <div className="space-y-4">
      {!hasKeys && (
        <Alert tone="warn" title="Потрібен Gemini API-ключ">
          Без ключа генерація неможлива.{' '}
          <button type="button" onClick={onNeedKeys} className="font-semibold underline underline-offset-2">
            Відкрити налаштування
          </button>{' '}
          і вставити ключі через кому — Worker вибере їх навмання й перейде на наступний при 429.
        </Alert>
      )}

      {/* Предмет визначає Gemini — кажуть про це до завантаження */}
      <div className="flex items-start gap-2.5 rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2.5 text-sm dark:border-brand-900 dark:bg-brand-950/30">
        <BrainCircuit className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
        <p className="text-slate-600 dark:text-slate-300">
          <b className="text-slate-900 dark:text-white">Предмет, тему й кількість визначить сам Gemini</b>{' '}
          за обсягом і щільністю матеріалу. Короткий скриншот зазвичай дає 3–8 щільних карток,
          а параграф або кілька сторінок PDF — 15–30+ карток без дублів і води. Визначені значення
          з’являться у вікні перегляду — там їх можна виправити перед збереженням.
        </p>
      </div>

      {/* Файли */}
      <Field
        label="Скриншоти конспекту або PDF"
        hint={`До ${MAX_PREVIEW_FILES} файлів, кожен до ${LIMITS.maxFileBytes / 1024 / 1024} МБ. Gemini прочитає зображення та текст із PDF.`}
      >
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            addFiles(e.dataTransfer.files)
          }}
          onClick={() => fileInputRef.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center transition ${
            dragging
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/30'
              : 'border-slate-300 hover:border-brand-400 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-900'
          }`}
        >
          <div className="flex gap-2 text-slate-400">
            <ImageIcon className="h-6 w-6" />
            <FileText className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium">Перетягніть файли сюди або натисніть, щоб обрати</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">PDF · PNG · JPEG · WEBP</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      </Field>

      {files.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900"
            >
              {f.isPdf ? (
                <FileText className="h-8 w-8 shrink-0 text-rose-500" />
              ) : (
                <img
                  src={f.dataUrl}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{f.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {(f.size / 1024).toFixed(0)} КБ
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                className="btn-ghost !px-1.5 !py-1"
                aria-label={`Видалити ${f.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Загалом {files.length} файл(ів), {(totalSize / 1024 / 1024).toFixed(2)} МБ
        </p>
      )}

      {/* Текст */}
      <Field
        label="Текст конспекту або тема"
        hint="Можна вставити текстом, додати файл, або обидва одразу — Gemini об’єднає їх в один запит і сам визначить предмет."
      >
        <textarea
          className="input min-h-32 resize-y font-mono text-[13px]"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={PLACEHOLDER}
          maxLength={LIMITS.maxTextChars}
        />
        <span className="mt-1 block text-right text-xs tabular-nums text-slate-400">
          {text.length} / {LIMITS.maxTextChars}
        </span>
      </Field>

      <Field label="Додаткові вказівки" hint="Напр.: «роби картки на дати», «тільки формули», «не більше 2 речень у відповіді».">
        <input
          className="input"
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          placeholder="необов’язково"
        />
      </Field>

      {error && (
        <Alert tone="error" title="Помилка генерації">
          {error}
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={generate} disabled={busy || (!text.trim() && !files.length)} className="btn-primary flex-1 sm:flex-none">
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Gemini думає…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" /> Згенерувати картки
            </>
          )}
        </button>

        {busy && (
          <button type="button" onClick={() => abortRef.current?.abort()} className="btn-ghost">
            <X className="h-4 w-4" /> Скасувати
          </button>
        )}

        {(text.trim() || files.length > 0) && !busy && (
          <button
            type="button"
            onClick={() => {
              setText('')
              setFiles([])
              setExtra('')
            }}
            className="btn-ghost"
          >
            <Trash2 className="h-4 w-4" /> Очистити
          </button>
        )}
      </div>

      <p className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Запит іде на ваш Cloudflare Worker, а ключі — лише в тілі запиту: вони не потрапляють у D1 і
          не записуються в логи. Модель: <code className="font-mono">{settings?.geminiModel}</code>.
        </span>
      </p>
    </div>
  )
}
